import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../services/api/axios.config";
import { notify } from "../utils/methods/notification";

const initialState = {
  coaFormCompleteList: {
    items: [],
    pageDetails: {
      total_count: 0,
      page: 1,
      per_page: 10,
    },
  },
  reManufactureRequestList: {
    items: [],
    pageDetails: {
      total_count: 0,
      page: 1,
      per_page: 10,
    },
  },
  coaFormCreateResponse: null,
  coaFormFailResponse: null,
  coaFormDetails: null,
  qaApprovedResponse: null,
  apheresisResponse: null, // New state for apheresis response
  testList: [],
  isLoading: false,
  isCreating: false,
  isFailing: false,
  isLoadingDetails: false,
  isLoadingTestList: false,
  isApprovingQA: false,
  isSelectingApheresis: false, // New loading state for apheresis
};

// For Fetching Approved LSM Listing
export const getQualityControlApprovedList = createAsyncThunk(
  "bmr/getQualityControlApprovedList", // Fixed: match slice name
  async (filters, { rejectWithValue }) => {
    try {
      const response = await api.post(`/coa-form/qc-approved/list`, filters);
      return response.data;
    } catch (error) {
      notify.error(error?.response?.data || error?.message || error)
  
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Fetching Approved LSM Listing
export const getReManufactureRequest = createAsyncThunk(
  "bmr/getReManufactureRequest", // Fixed: match slice name
  async (filters, { rejectWithValue }) => {
    try {
      const response = await api.post(`/product-manufacturing/re-manufacture-request/list`, filters);
      return response.data;
    } catch (error) {
      notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);



// For Fetching COA Form Details by ID
export const getQualityControlApprovedListById = createAsyncThunk(
  "bmr/getQualityControlApprovedListById", // Fixed: match slice name
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/coa-form/${id}/details`);
      return response.data;
    } catch (error) {
     notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Failing COA Form Under Quality Control
export const rejectQualityApproval = createAsyncThunk(
  "bmr/rejectQualityApproval", // Fixed: match slice name
  async (args, { rejectWithValue }) => {
    try {
      const { formData, id } = args
      const response = await api.put(
        `/coa-form/qc/fail/${id}`,
        formData
      );
      return response.data;
    } catch (error) {
    notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For QA Approval of COA Form
export const approveQualityAssurance = createAsyncThunk(
  "bmr/approveQualityAssurance", // Fixed: match slice name
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/coa-form/qa/approved`, formData);
      return response.data;
    } catch (error) {
      notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// For Qc lead Approval of COA Form
export const approveQcLead = createAsyncThunk(
  "bmr/QCapproved", // Fixed: match slice name
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.put(`/coa-form/qc-lead/approved/${formData?.coa_form_id}`, formData);
      return response.data;
    } catch (error) {
      notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

// New: For Selecting Apheresis (Product Manufacturing Approval)
export const selectApheresis = createAsyncThunk(
  "bmr/selectApheresis",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/product-manufacturing/approved`,
        formData
      );
      return response.data;
    } catch (error) {
     notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);
export const updateCoaForm = createAsyncThunk(
  "bmr/updateCoaForm",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/coa-form/update/${id}`, formData);
      return response.data;
    } catch (error) {
     notify.error(error?.response?.data || error?.message || error)
      return rejectWithValue(
        error.response ? error.response.data : error.message
      );
    }
  }
);

const qualityApprovalSlice = createSlice({
  name: "qualityApprovalList",
  initialState,
  reducers: {
    // Clear create response
    clearCoaFormCreateResponse: (state) => {
      state.coaFormCreateResponse = null;
    },
    // Clear fail response
    clearCoaFormFailResponse: (state) => {
      state.coaFormFailResponse = null;
    },
    // Clear QA approved response
    clearQaApprovedResponse: (state) => {
      state.qaApprovedResponse = null;
    },
    // Clear apheresis response
    clearApheresisResponse: (state) => {
      state.apheresisResponse = null;
    },
    // Clear form details
    clearCoaFormDetails: (state) => {
      state.coaFormDetails = null;
    },
    // Clear test list
    clearTestList: (state) => {
      state.testList = [];
    },
  },
  extraReducers: (builder) =>
    builder
      // Handling the state case for the LSM Approved listing
      .addCase(getQualityControlApprovedList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getQualityControlApprovedList.fulfilled, (state, action) => {
        // Structure the data to match what the component expects
        state.coaFormCompleteList = {
          items: action.payload?.data ?? [],
          pageDetails: action.payload?.pageDetails ?? {
            total_count: 0,
            page: 1,
            per_page: 10,
          },
        };
        state.isLoading = false;
      })
      .addCase(getQualityControlApprovedList.rejected, (state) => {
        state.isLoading = false;
      })

      // Handling the state case for the LSM Approved listing
      .addCase(getReManufactureRequest.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getReManufactureRequest.fulfilled, (state, action) => {
        // Structure the data to match what the component expects
        state.reManufactureRequestList = action.payload?.data
        state.isLoading = false;
      })

      .addCase(getReManufactureRequest.rejected, (state) => {
        state.isLoading = false;
      })
      // Handling COA Form Details by ID
      .addCase(getQualityControlApprovedListById.pending, (state) => {
        state.isLoadingDetails = true;
      })
      .addCase(getQualityControlApprovedListById.fulfilled, (state, action) => {
        state.coaFormDetails = action.payload?.data ?? null;
        state.isLoadingDetails = false;
      })
      .addCase(getQualityControlApprovedListById.rejected, (state) => {
        state.isLoadingDetails = false;
      })

      // Handling the state case for COA Form failure
      .addCase(rejectQualityApproval.pending, (state) => {
        state.isFailing = true;
      })
      .addCase(rejectQualityApproval.fulfilled, (state, action) => {
        state.coaFormFailResponse = action.payload;
        state.isFailing = false;
      })
      .addCase(rejectQualityApproval.rejected, (state) => {
        state.isFailing = false;
      })

      // Handling the state case for QA Approval
      .addCase(approveQualityAssurance.pending, (state) => {
        state.isApprovingQA = true;
      })
      .addCase(approveQualityAssurance.fulfilled, (state, action) => {
        state.qaApprovedResponse = action.payload;
        state.isApprovingQA = false;
      })
      .addCase(approveQualityAssurance.rejected, (state) => {
        state.isApprovingQA = false;
      })
      .addCase(updateCoaForm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCoaForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload.message;
        state.errorType = "success";
        state.error = true;
      })
      .addCase(updateCoaForm.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.payload.message;
        state.errorType = "error";
        state.error = true;
      })

      // Handling the state case for Apheresis Selection
      .addCase(selectApheresis.pending, (state) => {
        state.isSelectingApheresis = true;
      })
      .addCase(selectApheresis.fulfilled, (state, action) => {
        state.apheresisResponse = action.payload;
        state.isSelectingApheresis = false;
      })
      .addCase(selectApheresis.rejected, (state) => {
        state.isSelectingApheresis = false;
      }),
});

export const {
  clearCoaFormCreateResponse,
  clearCoaFormFailResponse,
  clearQaApprovedResponse,
  clearApheresisResponse,
  clearCoaFormDetails,
  clearTestList,
} = qualityApprovalSlice.actions;

// Fixed selectors
export const selectCoaFormCompleteList = (state) =>
  state.qualityApprovalList.coaFormCompleteList;
export const selectReManufactureRequestList = (state) =>
  state.qualityApprovalList.reManufactureRequestList;

export const selectCoaFormCreateResponse = (state) =>
  state.qualityApprovalList.coaFormCreateResponse;
export const selectCoaFormFailResponse = (state) =>
  state.qualityApprovalList.coaFormFailResponse;
export const selectQaApprovedResponse = (state) =>
  state.qualityApprovalList.qaApprovedResponse;
export const selectApheresisResponse = (state) =>
  state.qualityApprovalList.apheresisResponse;
export const selectCoaFormDetails = (state) =>
  state.qualityApprovalList.coaFormDetails;
export const selectTestList = (state) => state.qualityApprovalList.testList;
export const selectIsLoading = (state) => state.qualityApprovalList.isLoading; // Added missing selector
export const selectIsCreating = (state) => state.qualityApprovalList.isCreating;
export const selectIsFailing = (state) => state.qualityApprovalList.isFailing;
export const selectIsApprovingQA = (state) =>
  state.qualityApprovalList.isApprovingQA;
export const selectIsSelectingApheresis = (state) =>
  state.qualityApprovalList.isSelectingApheresis;
export const selectIsLoadingDetails = (state) =>
  state.qualityApprovalList.isLoadingDetails;
export const selectIsLoadingTestList = (state) =>
  state.qualityApprovalList.isLoadingTestList;

export default qualityApprovalSlice.reducer;
